import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, useWatch } from "react-hook-form";
import { XIcon } from "lucide-react";

import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { FieldGroup } from "@/components/ui/field";
import { SaveButton } from "@/components/ui/save-button";
import PageTitleBar from "@/components/PageTitleBar";
import { useAnalytics } from "@/providers/AnalyticsProvider";

import useBaseForm from "./hooks/useBaseForm";
import useFormErrors from "./hooks/useFormErrors";

/**
 * Drop-in replacement for `RuvixxForm` built on `react-hook-form` + Zod and
 * shadcn primitives.
 *
 * Fixes from RuvixxForm:
 * - `errorMessage` renders on initial mount (not just `componentDidUpdate`).
 * - Submit re-enables on reject without a 1s `setTimeout`.
 * - `extraButtons` renders with unique React keys.
 * - Double-submit is prevented by RHF's `formState.isSubmitting`.
 */
const BaseForm = ({
  schema,
  defaultValues,
  enablePageTitle = false,
  enablePageTitleActions = false,
  pageTitle,
  pageSubtitle,
  pageTitleActions = null,
  pageIcon,
  backLink,
  onSubmit,
  onSuccess,
  onCancel,
  onDelete,
  submitButtonText = "Save",
  submittingButtonText = "Saving",
  cancelButtonText = "Cancel",
  deleteButtonText = "Delete",
  submitButtonClassName = "long",
  cancelButtonClassName = "long",
  successHeader,
  successMessage,
  errorMessage,
  errorTitle = "",
  extraButtons,
  buttonsLeft = null,
  secondButton,
  shouldDisableSubmit,
  confirmSubmit = false,
  confirmText = "Do you want to submit this form?",
  ignoreEnterKey = false,
  actionBarPadding = 0,
  children,
}) => {
  const methods = useBaseForm({ schema, defaultValues });
  const watchedValues = useWatch({
    control: methods.control,
    disabled: !shouldDisableSubmit,
  });
  const { genericError, applyServerError, clearGenericError } =
    useFormErrors(methods);
  const { trackButtonClick } = useAnalytics();

  // Local dismissible mirrors of prop-driven banners. Reading directly from
  // the prop into local state via useEffect — including on the initial mount
  // — fixes RuvixxForm's componentDidUpdate-only bug where a form mounted
  // with `errorMessage` set never displayed the banner.
  const [externalError, setExternalError] = useState(errorMessage ?? null);
  const [externalSuccess, setExternalSuccess] = useState({
    header: successHeader ?? null,
    message: successMessage ?? null,
  });
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    setExternalError(errorMessage ?? null);
  }, [errorMessage]);

  useEffect(() => {
    setExternalSuccess({
      header: successHeader ?? null,
      message: successMessage ?? null,
    });
  }, [successHeader, successMessage]);

  const handleDismissError = useCallback(() => {
    setExternalError(null);
    clearGenericError();
  }, [clearGenericError]);

  const handleDismissSuccess = useCallback(() => {
    setExternalSuccess({ header: null, message: null });
    setSubmitSuccess(false);
  }, []);

  const runSubmit = useCallback(
    (submitFn = onSubmit) =>
      methods.handleSubmit(async values => {
        clearGenericError();
        setSubmitSuccess(false);
        try {
          if (submitFn) {
            await submitFn(values);
          }
          setSubmitSuccess(true);
          if (onSuccess) {
            await onSuccess(values);
          }
        } catch (err) {
          applyServerError(err);
        }
      }),
    [methods, onSubmit, onSuccess, applyServerError, clearGenericError]
  );

  const startSubmit = useCallback(
    e => {
      if (confirmSubmit) {
        setConfirmModalOpen(true);
        return;
      }
      runSubmit()(e);
    },
    [confirmSubmit, runSubmit]
  );

  const handlePrimaryClick = useCallback(
    e => {
      e.preventDefault();
      // Passing the event rather than a string lets the button's own text stand
      // as the label and lets a caller suppress it with data-analytics-ignore.
      trackButtonClick(e);
      startSubmit(e);
    },
    [startSubmit, trackButtonClick]
  );

  // The browser submits a form with a single text input on Enter even without
  // a submit button. Left unhandled, that native GET reloads the page with the
  // field values in the query string.
  const handleFormSubmit = useCallback(
    e => {
      e.preventDefault();
      if (onSubmit || onSuccess) startSubmit(e);
    },
    [onSubmit, onSuccess, startSubmit]
  );

  const handleConfirm = useCallback(
    e => {
      setConfirmModalOpen(false);
      runSubmit()(e);
    },
    [runSubmit]
  );

  const handleConfirmCancel = useCallback(() => {
    setConfirmModalOpen(false);
  }, []);

  const handleSecondButton = useCallback(
    e => {
      e.preventDefault();
      runSubmit(secondButton.onSubmit)(e);
    },
    [runSubmit, secondButton]
  );

  const handleKeyDown = useCallback(
    e => {
      if (!ignoreEnterKey) return;
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [ignoreEnterKey]
  );

  const isSubmitting = methods.formState.isSubmitting;
  const isSubmitBlocked = Boolean(shouldDisableSubmit?.(watchedValues));
  const errorBannerMessage = genericError ?? externalError;
  const showErrorBanner = !!errorBannerMessage;
  const showSuccessBanner =
    !!externalSuccess.header || !!externalSuccess.message;
  const successHeaderToRender = externalSuccess.header ?? "";
  const successMessageToRender = externalSuccess.message ?? "";

  // The primary submit (+ optional secondButton) always renders in the bottom
  // action bar, and additionally in the page title row when
  // enablePageTitleActions so it stays reachable at the top of long forms.
  const submitControls = (onSubmit || onSuccess) && (
    <>
      <SaveButton
        type="button"
        className={submitButtonClassName}
        saving={isSubmitting}
        disabled={isSubmitBlocked}
        savingText={submittingButtonText}
        onClick={handlePrimaryClick}
      >
        {submitButtonText}
      </SaveButton>
      {secondButton && (
        <Button
          type="button"
          variant="outline"
          disabled={secondButton.disabled || isSubmitting}
          onClick={handleSecondButton}
        >
          {secondButton.content}
        </Button>
      )}
    </>
  );

  return (
    <FormProvider {...methods}>
      {enablePageTitle && (
        <PageTitleBar
          backTo={backLink?.to}
          backLabel={backLink?.label}
          icon={pageIcon}
          title={pageTitle}
          subtitle={pageSubtitle}
          actions={
            enablePageTitleActions
              ? pageTitleActions || submitControls
              : undefined
          }
          className="tw:mb-5"
        />
      )}
      <form onSubmit={handleFormSubmit} onKeyDown={handleKeyDown} noValidate>
        <FieldGroup>{children}</FieldGroup>
        {showErrorBanner && (
          <Alert variant="destructive" className="tw:mt-4">
            <AlertTitle>
              {`Action Failed${errorTitle ? ": " + errorTitle : ""}`}
            </AlertTitle>
            <AlertDescription>{errorBannerMessage}</AlertDescription>
            <AlertAction>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Dismiss"
                onClick={handleDismissError}
              >
                <XIcon />
              </Button>
            </AlertAction>
          </Alert>
        )}
        {showSuccessBanner && submitSuccess && (
          <Alert className="tw:mt-4">
            {successHeaderToRender && (
              <AlertTitle>{successHeaderToRender}</AlertTitle>
            )}
            {successMessageToRender && (
              <AlertDescription>{successMessageToRender}</AlertDescription>
            )}
            <AlertAction>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Dismiss"
                onClick={handleDismissSuccess}
              >
                <XIcon />
              </Button>
            </AlertAction>
          </Alert>
        )}
        <div
          className="actionBar tw:mt-4 tw:flex tw:items-center tw:justify-between tw:gap-2"
          style={{ padding: actionBarPadding }}
        >
          <div className="buttonsLeft">{buttonsLeft}</div>
          <div className="tw:flex tw:items-center tw:gap-2">
            {extraButtons &&
              extraButtons.map((btn, idx) => (
                <span key={btn?.key ?? `extra-${idx}`}>{btn}</span>
              ))}
            {onCancel && (
              <Button
                type="button"
                variant="ghost"
                className={cancelButtonClassName}
                onClick={onCancel}
              >
                {cancelButtonText}
              </Button>
            )}
            {onDelete && (
              <Button type="button" variant="destructive" onClick={onDelete}>
                {deleteButtonText}
              </Button>
            )}
            {submitControls}
          </div>
        </div>
      </form>
      <Dialog
        open={confirmModalOpen}
        onOpenChange={open => {
          if (!open) handleConfirmCancel();
        }}
      >
        <DialogContent showCloseButton={false} className="tw:sm:max-w-sm">
          <DialogTitle className="tw:sr-only">Confirm</DialogTitle>
          <div>{confirmText}</div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleConfirmCancel}
            >
              {cancelButtonText}
            </Button>
            <Button type="button" onClick={handleConfirm}>
              {submitButtonText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FormProvider>
  );
};

BaseForm.propTypes = {
  schema: PropTypes.object,
  defaultValues: PropTypes.object,
  enablePageTitle: PropTypes.bool,
  enablePageTitleActions: PropTypes.bool,
  pageTitle: PropTypes.string,
  pageSubtitle: PropTypes.string,
  pageTitleActions: PropTypes.node,
  pageIcon: PropTypes.node,
  backLink: PropTypes.shape({
    to: PropTypes.string,
    label: PropTypes.string,
  }),
  onSubmit: PropTypes.func,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  onDelete: PropTypes.func,
  submitButtonText: PropTypes.string,
  submittingButtonText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  deleteButtonText: PropTypes.string,
  submitButtonClassName: PropTypes.string,
  cancelButtonClassName: PropTypes.string,
  successHeader: PropTypes.string,
  successMessage: PropTypes.string,
  errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  errorTitle: PropTypes.string,
  extraButtons: PropTypes.arrayOf(PropTypes.node),
  buttonsLeft: PropTypes.node,
  secondButton: PropTypes.shape({
    content: PropTypes.node,
    disabled: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
  }),
  shouldDisableSubmit: PropTypes.func,
  confirmSubmit: PropTypes.bool,
  confirmText: PropTypes.string,
  ignoreEnterKey: PropTypes.bool,
  actionBarPadding: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node,
};

export default BaseForm;
